import React, { useEffect, useMemo, useState, useCallback } from "react";
import "../css/overview.css";
import { v4 as uuidv4 } from "uuid";
import {
  BACKGROUND_COLOR,
  BORDER_COLOR,
  BORDER_WIDTH,
  ETRADE_URL,
  isFirstYearOfComp,
  renderNetIncome,
  toDollar,
  dataKeyMap,
  formatMeasure,
  payRatioCalculatedMessage,
  emdash,
  MIN_YEAR,
  RadioSelector,
} from "../utils/Constants/systemSetting";
import BarGraphOverview from "../components/Graphs/BarGraphOverview";
import LineGraph from "../components/Graphs/LineGraph";
import dataFilter from "../utils/DataFilter/dataFilter";
import { useSelector } from "react-redux";
import PuffLoader from "react-spinners/PuffLoader";
import { Popover, Checkbox, Button, Select, Radio, Tooltip } from "antd";
import { Link, useLocation } from "react-router-dom";
import {
  getCompanyProfile,
  getFiscalData,
  getParagraphsFrom8k502,
} from "../redux/actions/CompanyAction";
import { useDispatch } from "react-redux";
import slugifyName from "../utils/slugifyName";
import NotFound from "./notFound";
import { getExecPosition } from "../utils/ExecutiveData/getExecPosition";
import DisclosureSearchBar from "../components/DisclosureSearchBar.js";
import CompanyCalculator from "../components/CompanyCalculator.js";
import BoardPay from "../components/BoardPay";
import PeerGroupAnalysis from "../components/PeerGroupAnalysis.js";
import IncentiveStructure from "../components/IncentiveStructure.js";
import Calculator10b51 from "../components/Calculator10b51.js";
import { exportToCSV } from "../utils/ExecutiveData/csvExport";
import ExportPDFButton from "../components/ExportPDFButton.js";
import OptionsCalculators from "../components/OptionsCalculators.js";
import CompAI from "../components/CompAI.js";
import RTSRCalculators from "../components/RTSRCalculators.js";
import RiskModule from "../components/RiskModule.js";
import CompensationDecisions from "../components/CompensationDecisions.js";
import PlanBasedAwards from "../components/PlanBasedAwards.js";
import BoardPayStructure from "../components/BoardPayStructure.js";
import ExecutivePerks from "../components/ExecutivePerks.js";
import VotingOutcomes from "../components/VotingOutcomes.js";
import ChangeInControls from "../components/SeveranceChangeInControl.js";
import {
  getExecPositionGroup,
  getExecPositionScore,
} from "../utils/ExecutiveData/getExecPosition.mjs";
import EquityPoolUsage from "../components/EquityPoolUsage.js";
import TargetTotalDirectCompensation from "../components/TargetTotalDirectCompensation.js";
import CompanyFinancials from "../components/CompanyFinancials.js";

const pageModeOptions = [
  "Company Overview",
  "Company Calculators",
  "Company Financials",
  "Peer Group Analysis",
  "Voting Outcomes",
  "Board Pay",
  "Board Pay Structure",
  "Executive Perks",
  "Options Calculator",
  "rTSR Calculators",
  "Incentive Structure",
  "Compensation Decisions",
  "Grants of Plan-Based Awards",
  "Risk",
  "Severance/Change in Control",
  "Equity Pool Usage",
  "Target Total Direct Compensation",
  "Comp AI®",
  // "Equity Compensation Modeling (ECM)",
];

const needsReportFiscalYearSelector = [
  "Executive Perks",
  "Board Pay Structure",
  "Incentive Structure",
  "Compensation Decisions",
  "Grants of Plan-Based Awards",
  "Risk",
  "Severance/Change in Control",
  "Target Total Direct Compensation",
];

const hoverTextMap = {
  "Company Overview":
    "See a breakdown of compensation components for all named executive officers of a given firm for the last five years, along with the original source data, if needed. Disclosure search and recent compensation changes and events are also covered.",
  "Company Financials":
    "Available on C-Suite Comp® Enterprise. See income statements, cash flow, adjusted metrics and financial ratios for a given firm.",
  "Voting Outcomes":
    "Available on C-Suite Comp® Enterprise. See company proposals and voting outcome results based on 8-K Item 5.07.",
  "Executive Perks":
    "Available on C-Suite Comp® Enterprise. View company-wide and executive-specific perquisites breakdowns and valuations. Compare executive perquisites with peer companies.",
  "Company Calculators":
    "Available on C-Suite Comp® Enterprise: Calculate the TSR graphic for your company and your peer group (item 201 compliant) and the Pay-for-Performance tables required in your proxy. You can also gather market intelligence on an appropriate Company Selected Metric for your firm.",
  "Peer Group Analysis":
    "Available on C-Suite Comp® Enterprise: Evaluate which firms to consider when forming a peer group for reporting purposes. You can examine mutual peers, distant peers, and reverse peers. Graphics are provided for how your firm stands in comparison to peers across a range of financial and operating metrics.",
  "Board Pay":
    "Available on C-Suite Comp® Enterprise: Identify board members and their compensation by year. Board profiles and committees represented are included.",
  "Options Calculator":
    "Available on C-Suite Comp® Enterprise: Value executive stock options at any point in time using the appropriate risk-free rate. This module can also help estimate past return volatility, a required input into option.",
  "rTSR Calculators":
    "Available on C-Suite Comp® Enterprise: Estimate an ex-ante valuation of a performance share awards where the number of shares awarded to an executive is a function of how the stock price performs relative to a peer group. Can be utilized for both planning purposes or while a given program is in place.",
  "Equity Compensation Modeling (ECM)":
    "Available on C-Suite Comp® Enterprise: Model or plan stock and option sales under a SEC Rule 10b5-1 trading plan.",
  "Incentive Structure":
    "Available on C-Suite Comp® Enterprise: View short- and long-term incentive plans, including minimum, target, and maximum values. Comparison with peer companies.",
  "Board Pay Structure":
    "Available on C-Suite Comp® Enterprise: View board member types, compensation, and perquisites. Compare board pay structure with peer companies.",
  Risk: "Available on C-Suite Comp® Enterprise: Highlights changes in the company's risk section compared to the previous year.",
  "Compensation Decisions":
    "Available on C-Suite Comp® Enterprise: Details future executive pay adjustments made for the upcoming year.",
  "Severance/Change in Control":
    "Available on C-Suite Comp® Enterprise: Displays executive-specific and company-wide severance/change-in-control triggers, benefits, and governance policies. Compare severance/change in control values with peer companies.",
  "Grants of Plan-Based Awards":
    "Available on C-Suite Comp® Enterprise: Shows awards granted under incentive plans, including grant date fair value, performance metrics, and payout potential.",
  "Equity Pool Usage":
    "Available on C-Suite Comp® Enterprise: View all awards granted, vested, exercised, forfeited, and outstanding awards. Calculate burn rate and plan overhang percentages and compare equity pool usage with peer companies.",
  "Target Total Direct Compensation":
    "Available on C-Suite Comp® Enterprise: Review compensation targets for salary, bonus, and long-term incentives (LTIs). Evaluate how the LTI award is split this company.",
  "Comp AI®":
    "Available on C-Suite Comp® Enterprise: Comp AI® is our in-house developed generative AI tool which allows you to quickly ask key questions regarding a firm’s various SEC documents to glean key information, potentially saving you countless hours of effort. The tool not only provide key answers but provides relevant page citations.",
};

// These are used to navigate to the page directly, add these as pagmodeoptions are added
// (overview is default view, so it is not needed here)
const hashMap = {
  calculators: "Company Calculators",
  financials: "Company Financials",
  "executive-perks": "Executive Perks",
  "voting-outcomes": "Voting Outcomes",
  peers: "Peer Group Analysis",
  board: "Board Pay",
  "board-structure": "Board Pay Structure",
  options: "Options Calculator",
  rtsr: "rTSR Calculators",
  "equity-compensation-modeling": "Equity Compensation Modeling (ECM)",
  "incentive-structure": "Incentive Structure",
  risk: "Risk",
  "compensation-decisions": "Compensation Decisions",
  "plan-based-awards": "Grants of Plan-Based Awards",
  "severance-cic": "Severance/Change in Control",
  "equity-pool-usage": "Equity Pool Usage",
  "target-total-comp": "Target Total Direct Compensation",
  "comp-ai": "Comp AI®",
};

const reverseHashMap = {};
Object.entries(hashMap).forEach(([key, val]) => {
  reverseHashMap[val] = key;
});

export default function CompanyOverview() {
  const version = localStorage.getItem("version") || "1.0.0";

  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState("2021");
  // const { info, executives, marketCap } = useSelector(
  //   (state) => state.CompanyReducer
  // );
  const user = useSelector((state) => state.UserReducer);
  const { subscription } = useSelector((state) => state.CustomerReducer);

  const isTier2 = useMemo(() => {
    if (user?.tier2access || subscription?.plan?.metadata?.isTier2 === "true") {
      return true;
    }
    return false;
  }, [user, subscription]);

  const peerGroupAccess = useMemo(() => {
    if (subscription?.plan?.metadata?.peerGroupAccess === "true") {
      return true;
    }
    return false;
  }, [subscription]);

  const companyCalculatorAccess = useMemo(() => {
    if (subscription?.plan?.metadata?.companyCalculatorAccess === "true") {
      return true;
    }
    return false;
  }, [subscription]);

  const equityPoolAccess = useMemo(() => {
    if (subscription?.plan?.metadata?.equityPoolAccess === "true") {
      return true;
    }
    return false;
  }, [subscription]);

  const compAIaccess = useMemo(() => {
    if (user?.compAIaccess) {
      return true;
    }
    return false;
  }, [user]);

  // Use CompAIaccess as a proxy for having 10b5-1 access (same list of exclusive users)
  if (
    compAIaccess &&
    !pageModeOptions.includes("Equity Compensation Modeling (ECM)")
  ) {
    pageModeOptions.push("Equity Compensation Modeling (ECM)");
  }

  const [companyNotFound, setCompanyNotFound] = useState(false);
  const [PDFprocessing, setPDFprocessing] = useState(false);

  const [customUserPeerGroups, setCustomUserPeerGroups] = useState(
    user?.customPeerGroups || {},
  );

  useMemo(() => {
    if (user?.customPeerGroups && Object.keys(user?.customPeerGroups).length) {
      setCustomUserPeerGroups(user?.customPeerGroups);
    }
  }, [user?.customPeerGroups]);

  const { companies, marketCap: marketCaps } = useSelector(
    (state) => state.CompaniesReducer,
  );
  const { paragraphsFrom8k502, companyProfile, financialData } = useSelector(
    (state) => state.CompanyReducer,
  );
  const { csuite: allExecutives } = useSelector(
    (state) => state.ExecutivesReducer,
  );
  const [info, setInfo] = useState();
  const [executives, setExecutives] = useState([]);
  const [analystCohortExecutives, setAnalystCohortExecutives] = useState([]);
  const [proxyCohortExecutives, setProxyCohortExecutives] = useState([]);
  const [compensationCohortExecutives, setCompensationCohortExecutives] =
    useState([]);
  const [performanceCohortExecutives, setPerformanceCohortExecutives] =
    useState([]);
  const [compAIProxyCohortExecutives, setCompAIProxyCohortExecutives] =
    useState([]);
  const [
    compAIPerformanceCohortExecutives,
    setCompAIPerformanceCohortExecutives,
  ] = useState([]);
  const [
    compAICompensationCohortExecutives,
    setCompAICompensationCohortExecutives,
  ] = useState([]);
  const [customCohortExecutives, setCustomCohortExecutives] = useState([]);
  const [CSVExportOptions, setCSVExportOptions] = useState([
    { value: "company", label: "Company" },
  ]);
  const [CSVExportOptionSelected, setCSVExportOptionSelected] =
    useState("company");
  const [marketCap, setMarketCap] = useState();
  const [includesSARaward, setIncludesSARaward] = useState(false);
  const [includesRSUs, setIncludesRSUs] = useState(false);
  const [includesPSUs, setIncludesPSUs] = useState(false);
  const [yearsWithData, setYearsWithData] = useState([]);
  const [dataPeekLinks, setDataPeekLinks] = useState(<div></div>);
  const [payRatioIsCalculated, setPayRatioIsCalculated] = useState(false);
  const [pageModeSelected, setPageModeSelected] = useState(
    "Company Calculators",
  );

  const [showRankWithinCompany, setShowRankWithinCompany] = useState(false);
  const [
    tableContainsRowDoesNotEqualTotal,
    setTableContainsRowDoesNotEqualTotal,
  ] = useState(false);

  const [showShortDescription, setShowShortDescription] = useState(true);

  const location = useLocation();

  const formatExecPositionGroup = (positionGroups) => {
    if (positionGroups.includes("N/A")) return ["N/A"];
    const formattedPositons = [];
    for (const positionGroup of positionGroups) {
      for (const [key, value] of Object.entries(positionGroup)) {
        if (key === "formatted") {
          formattedPositons.push(value);
        }
      }
    }
    return formattedPositons;
  };

  const getBestExecPosGroup = (executive) => {
    const positions = getExecPosition(executive, year);
    const execPosGroups = [];
    for (const pos of positions) {
      execPosGroups.push(getExecPositionGroup(pos));
    }
    const chiefPositions = [];
    let bestScore = Infinity; //lowest is best
    let bestPos = "";
    for (const execPosGroup of execPosGroups) {
      if (execPosGroup === "N/A") continue;
      let currentScore = getExecPositionScore(execPosGroup);
      if (execPosGroup.formatted.includes("Chief ")) {
        chiefPositions.push(execPosGroup);
      }
      if (currentScore < bestScore) {
        bestScore = currentScore;
        bestPos = execPosGroup;
      }
    }
    // For all matching positions:
    // setExecPositionGroup(execPosGroup);
    // For just the "best" position(s):
    return chiefPositions.length
      ? chiefPositions
      : bestPos
        ? [bestPos]
        : ["N/A"];
  };

  // TODO: Separate CSV export Component //
  const getCompensationTableKeysAndYears = (execs) => {
    const execTableKeys = [];
    const yearsPossible = [];
    for (let yr = new Date().getFullYear(); yr >= MIN_YEAR; yr--) {
      yearsPossible.push(yr);
    }
    const yearsAvailable = [];
    for (const exec of execs) {
      for (const yearPossible of yearsPossible) {
        if (!exec?.compensation?.[yearPossible]) continue;
        if (!yearsAvailable.includes(yearPossible)) {
          yearsAvailable.push(yearPossible);
        }

        for (const key of Object.keys(exec.compensation[yearPossible])) {
          if (
            !execTableKeys.includes(key) &&
            key !== "rank" &&
            key !== "undefined" &&
            key !== "originalTotal" &&
            key !== "manuallyEdited" &&
            key !== "sumOfCompensationComponents" &&
            exec.compensation[yearPossible][key] > 0
          ) {
            execTableKeys.push(key);
          }
        }
      }
    }
    execTableKeys.sort((a, b) => dataKeyMap[a].order - dataKeyMap[b].order);
    return [execTableKeys, yearsAvailable];
  };

  const formatExecutiveDataForExport = (
    executivesData,
    includeCompanyInfo = false,
  ) => {
    const [compTableKeys, yearsAvailable] =
      getCompensationTableKeysAndYears(executivesData);
    const allData = [];
    for (const exec of executivesData) {
      for (const yearAvailable of yearsAvailable) {
        let sumOfCompensationComponents = 0;
        const compDataForYear = {};
        for (const compType of compTableKeys) {
          const compensationForType =
            exec?.compensation?.[yearAvailable]?.[compType];
          if (compType !== "total") {
            sumOfCompensationComponents +=
              exec?.compensation?.[yearAvailable]?.[compType] || 0;
          }
          compDataForYear[dataKeyMap[compType].title] =
            !compensationForType && compensationForType !== 0
              ? ""
              : compensationForType === emdash
                ? "0"
                : compensationForType;
        }
        if (Object.values(compDataForYear).every((val) => val === "")) {
          continue;
        }
        const execPosition = getExecPosition(exec, yearAvailable);
        //exec.position?.[yearAvailable]
        const newExecutiveData = {
          Name: `"${exec.name}"`,
          "Principal Position(s)": execPosition
            ? `"${execPosition?.join(", ")}"`
            : "",
          "Position Group": formatExecPositionGroup(getBestExecPosGroup(exec)),
          Year: yearAvailable,
          ...compDataForYear,
        };
        //Check if total matches sum
        if (
          newExecutiveData["Total Compensation"] &&
          Math.abs(
            parseInt(
              newExecutiveData["Total Compensation"] -
                sumOfCompensationComponents,
            ),
          ) > 2
        ) {
          newExecutiveData["Sum of Compensation Components"] = parseInt(
            sumOfCompensationComponents,
          );
          newExecutiveData["Difference (Reported Total - Sum of Components)"] =
            parseInt(
              newExecutiveData["Total Compensation"] -
                sumOfCompensationComponents,
            );
          // ensure key exists for first entry for headers:
          if (
            allData.length &&
            !allData[0]?.["Sum of Compensation Components"]
          ) {
            allData[0]["Sum of Compensation Components"] = "";
            allData[0]["Difference (Reported Total - Sum of Components)"] = "";
          }
        }
        if (includeCompanyInfo) {
          const execDataWithCompanyInfo = {
            Ticker: exec.Ticker,
            Company: `"${exec?.company}"`,
            ...newExecutiveData,
          };
          allData.push(execDataWithCompanyInfo);
        } else {
          allData.push(newExecutiveData);
        }
      }
    }
    return allData;
  };
  // End of CSV export //

  // console.log(info);

  const isCustomPeerGroup = useCallback(
    (peerGroupKey) => {
      if (
        Object.keys(customUserPeerGroups).length &&
        Object.keys(customUserPeerGroups).some((key) => key === peerGroupKey)
      ) {
        return true;
      }
      return false;
    },
    [customUserPeerGroups],
  );

  useEffect(() => {
    const id = location.pathname
      .split("/")
      [location.pathname.split("/").length - 1]?.toUpperCase();

    const token = localStorage.getItem("ACCESS_TOKEN");

    const ticker = info?.Ticker;
    const CIK = info?.CIK;
    if (CIK && ticker) {
      dispatch(getParagraphsFrom8k502(CIK));

      dispatch(getCompanyProfile([ticker], [CIK], token));
      setShowShortDescription(true);

      dispatch(
        getFiscalData(
          "Current Market Cap",
          [info?.Ticker],
          [info?.CIK], //CIKs
          "latest", //periodType
          null, //periodsNeeded
          "USD",
          true,
          token,
        ),
      );
    }

    if (info?.Ticker !== id && Object.keys(companies)?.length <= 100) {
      setLoading(true);
    }

    if (!companies?.[id] && Object.keys(companies)?.length > 100) {
      setLoading(false);
      setCompanyNotFound(true);
    } else {
      setCompanyNotFound(false);
    }

    // Navigate to correct subpage for Tier 2 users
    const hash = window.location.hash.substring(1);
    if (hashMap?.[hash]) {
      setPageModeSelected(hashMap?.[hash]);
    } else {
      setPageModeSelected("Company Overview");
    }

    setInfo(companies[id]);
    setTableContainsRowDoesNotEqualTotal(false);

    for (const company of marketCaps) {
      if (company.Ticker === id) {
        setMarketCap(company?.marketCap);
        break;
      }
    }

    const executiveData = [];
    for (const executive of allExecutives) {
      if (executive.Ticker === id) {
        executiveData.push(executive);
      }
    }

    if (info?.AnalystCohort?.length) {
      setAnalystCohortExecutives(
        allExecutives.filter((exec) =>
          info.AnalystCohort.some((ticker) => ticker === exec.Ticker),
        ),
      );
    } else {
      setAnalystCohortExecutives([]);
    }

    if (info?.ProxyCohort?.length) {
      setProxyCohortExecutives(
        allExecutives.filter((exec) =>
          info.ProxyCohort.some((ticker) => ticker === exec.Ticker),
        ),
      );
    } else {
      setProxyCohortExecutives([]);
    }

    if (info?.ProxyCohortCompensation?.length) {
      setCompensationCohortExecutives(
        allExecutives.filter((exec) =>
          info.ProxyCohortCompensation.some((ticker) => ticker === exec.Ticker),
        ),
      );
    } else {
      setCompensationCohortExecutives([]);
    }

    if (info?.ProxyCohortPerformance?.length) {
      setPerformanceCohortExecutives(
        allExecutives.filter((exec) =>
          info.ProxyCohortPerformance.some((ticker) => ticker === exec.Ticker),
        ),
      );
    } else {
      setPerformanceCohortExecutives([]);
    }

    if (info?.CompAIProxyCohort?.length) {
      setCompAIProxyCohortExecutives(
        allExecutives.filter((exec) =>
          info.CompAIProxyCohort.some((ticker) => ticker === exec.Ticker),
        ),
      );
    } else {
      setCompAIProxyCohortExecutives([]);
    }

    if (info?.CompAIProxyCohortCompensation?.length) {
      setCompAICompensationCohortExecutives(
        allExecutives.filter((exec) =>
          info.CompAIProxyCohortCompensation.some(
            (ticker) => ticker === exec.Ticker,
          ),
        ),
      );

      if (info?.CompAIProxyCohortPerformance?.length) {
        setCompAIPerformanceCohortExecutives(
          allExecutives.filter((exec) =>
            info.CompAIProxyCohortPerformance.some(
              (ticker) => ticker === exec.Ticker,
            ),
          ),
        );
      } else {
        setCompAIPerformanceCohortExecutives([]);
      }
    } else {
      setCompAICompensationCohortExecutives([]);
    }

    // if (
    //   isCustomPeerGroup(CSVExportOptionSelected) &&
    //   CSVExportOptions.find((item) => item.label === CSVExportOptionSelected)
    //     .tickers
    // ) {
    //   const customTickersNeeded = console.log(customTickersNeeded);
    //   if (customTickersNeeded.length) {
    //     setCustomCohortExecutives(
    //       allExecutives.filter((exec) =>
    //         CSVExportOptions.find(
    //           (item) => item.label === CSVExportOptionSelected
    //         ).tickers.includes(exec.Ticker)
    //       )
    //     );
    //   } else {
    //     setCustomCohortExecutives([]);
    //   }
    // }

    // console.log("8k 502s", paragraphsFrom8k502);
    // console.log("companies", companies);
    // console.log("execs", allExecutives);
    // console.log("marketCaps", marketCaps);

    setExecutives(executiveData);
    if (
      allExecutives.length &&
      Object.keys(companies).length > 7 &&
      info?.Ticker === id
    ) {
      setLoading(false);
    }

    setIncludesSARaward(false);
    setIncludesRSUs(false);
    setIncludesPSUs(false);

    setCSVExportOptionSelected("company");
  }, [info, allExecutives, companies, marketCaps, location.pathname, dispatch]);

  useEffect(() => {
    const CSVoptions = [];
    CSVoptions.push({
      value: "company",
      label: "Company",
      disabled: executives.length === 0,
    });
    CSVoptions.push({
      value: "analyst",
      label: "Analyst Cohort",
      disabled: analystCohortExecutives.length === 0,
    });
    if (info?.ProxyCohort) {
      CSVoptions.push({
        value: "proxy",
        label: "Proxy Cohort",
        disabled: proxyCohortExecutives.length === 0,
      });
    }

    if (info?.ProxyCohortCompensation) {
      CSVoptions.push({
        value: "proxyCompensation",
        label: "Proxy Cohort (Compensation)",
        disabled: compensationCohortExecutives.length === 0,
      });
    }
    if (info?.ProxyCohortPerformance) {
      CSVoptions.push({
        value: "proxyPerformance",
        label: "Proxy Cohort (Performance)",
        disabled: performanceCohortExecutives.length === 0,
      });
    }

    if (info?.CompAIProxyCohort) {
      CSVoptions.push({
        value: "compAIproxy",
        label: "Comp AI Proxy Cohort",
        disabled: compAIProxyCohortExecutives.length === 0,
      });
    }

    if (info?.CompAIProxyCohortCompensation) {
      CSVoptions.push({
        value: "compAIproxyCompensation",
        label: "Comp AI Proxy Cohort (Compensation)",
        disabled: compAICompensationCohortExecutives.length === 0,
      });
    }
    if (info?.CompAIProxyCohortPerformance) {
      CSVoptions.push({
        value: "compAIproxyPerformance",
        label: "Comp AI Proxy Cohort (Performance)",
        disabled: compAIPerformanceCohortExecutives.length === 0,
      });
    }

    if (customUserPeerGroups && Object.keys(customUserPeerGroups).length) {
      for (const [customPeerGroupKey, customPeerGroupValue] of Object.entries(
        customUserPeerGroups,
      )) {
        CSVoptions.push({
          value: customPeerGroupKey,
          label: customPeerGroupKey,
          tickers: customPeerGroupValue,
        });
      }
    }

    setCSVExportOptions(CSVoptions);
  }, [
    info,
    executives,
    analystCohortExecutives,
    proxyCohortExecutives,
    compensationCohortExecutives,
    performanceCohortExecutives,
    customUserPeerGroups,
    compAIProxyCohortExecutives,
    compAIPerformanceCohortExecutives,
    compAICompensationCohortExecutives,
  ]);

  const getDatapoint = useCallback(
    (datapoint) => {
      /* Allows for getting most recent year */
      if (
        info?.[datapoint]?.[year?.toString()]?.value ||
        info?.[datapoint]?.[year?.toString()]?.value === 0 // revenue can be 0
      ) {
        return {
          year: year,
          value: info?.[datapoint]?.[year?.toString()]?.value,
          calculated: info?.[datapoint]?.[year?.toString()]?.calculated
            ? true
            : false,
        };
      }
      // }
      return { year: "", value: "N/A" };
    },
    [info, year],
  );

  const getMeasure = useCallback(
    (measureName) => {
      /* Allows for getting most recent year */
      if (
        info?.Measures?.[measureName]?.[year?.toString()] ||
        info?.Measures?.[measureName]?.[year?.toString()] === 0 // revenue can be 0
      ) {
        return {
          year: year,
          value: info?.Measures?.[measureName]?.[year?.toString()],
        };
      }
      // }
      return { year: "", value: "N/A" };
    },
    [info, year],
  );

  const [positions, setPositions] = useState([...new Set()]);

  useEffect(() => {
    const getClosestYear = (executives, year) => {
      const yearsAvailable = [];
      executives?.forEach((exe, index) => {
        for (const [key, value] of Object.entries(exe?.["position"])) {
          if (value?.length && !yearsAvailable.includes(key)) {
            yearsAvailable.push(key);
          }
        }
      });
      // Can return years if only one option or if a year available === current query
      if (yearsAvailable.length === 1) return yearsAvailable[0];
      if (yearsAvailable.includes(year)) return year;

      // Otherwise, find the most relevent year:
      let currentBest = {};
      for (const dataYear of yearsAvailable) {
        let score = dataYear - year;
        if (
          !currentBest?.score ||
          (score > currentBest?.score && score <= 1) ||
          (score < currentBest?.score && score >= 1)
        ) {
          currentBest = { score, year: dataYear };
        }
      }
      return currentBest?.year;
    };
    let tempPositions = [];
    let closestYear = getClosestYear(executives, year);
    executives?.forEach((exe, index) => {
      let posFound = false;

      for (const [key, value] of Object.entries(exe?.["position"])) {
        if (key === closestYear) {
          posFound = true;
          tempPositions.push(value);
        }
      }
      if (!posFound) tempPositions.push(["N/A"]);

      if (index == executives.length - 1) {
        setPositions([...new Set(tempPositions)]);
      }
    });
  }, [year, executives]);

  const [lineGroup, setLineGroup] = useState({});
  useMemo(() => {
    const yearsArray = () => {
      const years = [];
      for (let year = new Date().getFullYear(); year >= MIN_YEAR; year--) {
        years.push(year);
      }
      return years;
    };
    const years = yearsArray();
    const newLineGroup = {};
    // Needed to put data in same format as homepage line graph
    for (let y in years) {
      const newData = new dataFilter(executives, year, companies);
      newLineGroup[years[y]] = newData.data;
    }
    setLineGroup(newLineGroup);
  }, [year, executives, companies]);

  const fiscalMarketCap = useMemo(() => {
    return financialData?.["Current Market Cap"]?.[info?.Ticker]?.result?.[0]
      ?.ratio;
  }, [financialData, info]);

  const renderCompensation = useMemo(() => {
    return executives
      ?.sort((a, b) => {
        const aKeys = Object.keys(a?.compensation);
        const bKeys = Object.keys(b?.compensation);
        let sortVal = Math.max(...bKeys) - Math.max(...aKeys);
        if (sortVal === 0) {
          sortVal = bKeys.length - aKeys.length;
        }
        return sortVal;
      })
      ?.map((exe, exeIndex) => {
        // console.log(exe);
        const row = exe?.compensation
          ? Object.entries(exe?.compensation).length
          : 1;
        // if (exe?.compensation) {
        return Object.entries(exe?.compensation)
          .slice(0)
          .reverse()
          .map((item, index) => {
            const sumTotal =
              (item[1].salary || 0) +
              (item[1].bonus || 0) +
              (item[1].stockAward || 0) +
              (item[1].RSUs || 0) +
              (item[1].PSUs || 0) +
              (item[1].optionAward || 0) +
              (item[1]?.SARaward || 0) +
              (item[1].nonEquity || 0) +
              (item[1].pension || 0) +
              (item[1].other || 0);
            if (!includesSARaward && item[1]?.SARaward > 0) {
              setIncludesSARaward(true);
            }
            if (!includesRSUs && item[1]?.RSUs > 0) {
              setIncludesRSUs(true);
            }
            if (!includesPSUs && item[1]?.PSUs > 0) {
              setIncludesPSUs(true);
            }
            let rowDoesNotEqualTotal = false;
            if (
              sumTotal &&
              !isNaN(parseInt(item[1]?.["total"])) &&
              (sumTotal + 2 <= parseInt(item[1]?.["total"]) ||
                sumTotal - 2 >= parseInt(item[1]?.["total"]))
            ) {
              rowDoesNotEqualTotal = true;
              setTableContainsRowDoesNotEqualTotal(true);
            }
            return (
              <tr
                key={index}
                style={{
                  borderColor: PDFprocessing ? "rgb(0,0,0,0.01)" : "",
                }}
              >
                {index == 0 && (
                  <td
                    className="executive"
                    rowSpan={row}
                    style={{ verticalAlign: "middle" }}
                  >
                    <Link
                      to={`/company/${info?.Ticker}/${slugifyName(exe?.name)}`}
                      style={{ color: "black" }}
                    >
                      <p className="executive-name">
                        {isFirstYearOfComp(exe, year)
                          ? exe?.name + "*"
                          : exe?.name}
                      </p>
                      <div className="title">
                        <>
                          {exe?.compensation?.[year] ? (
                            positions[exeIndex]?.map((pos, index) => (
                              <p key={index}>{pos}</p>
                            ))
                          ) : (
                            <p key={index}>No position in {year}</p>
                          )}
                        </>
                        {showRankWithinCompany &&
                          exe?.compensation?.[year]?.rank && (
                            <p key={index}>
                              Rank: {exe?.compensation?.[year]?.rank}
                            </p>
                          )}
                      </div>
                    </Link>
                  </td>
                )}

                <td
                  className="compensation"
                  style={{ fontWeight: item[0] === year ? "bold" : "" }}
                >
                  {item[0]}
                </td>
                <td
                  className="compensation d-none d-md-table-cell"
                  style={{ fontWeight: item[0] === year ? "bold" : "" }}
                >
                  {toDollar(parseInt(item[1]?.salary)) || emdash}
                </td>
                <td
                  className="compensation d-none d-md-table-cell"
                  style={{ fontWeight: item[0] === year ? "bold" : "" }}
                >
                  {toDollar(parseInt(item[1]?.bonus)) || emdash}
                </td>
                <td
                  className="compensation d-none d-md-table-cell"
                  style={{ fontWeight: item[0] === year ? "bold" : "" }}
                >
                  {toDollar(parseInt(item[1]?.["stockAward"])) || emdash}
                </td>
                {includesRSUs && (
                  <td
                    className="compensation d-none d-md-table-cell"
                    style={{ fontWeight: item[0] === year ? "bold" : "" }}
                  >
                    {toDollar(parseInt(item[1]?.["RSUs"])) || emdash}
                  </td>
                )}
                {includesPSUs && (
                  <td
                    className="compensation d-none d-md-table-cell"
                    style={{ fontWeight: item[0] === year ? "bold" : "" }}
                  >
                    {toDollar(parseInt(item[1]?.["PSUs"])) || emdash}
                  </td>
                )}
                <td
                  className="compensation d-none d-md-table-cell"
                  style={{ fontWeight: item[0] === year ? "bold" : "" }}
                >
                  {toDollar(parseInt(item[1]?.["optionAward"])) || emdash}
                </td>
                {includesSARaward && (
                  <td
                    className="compensation d-none d-md-table-cell"
                    style={{ fontWeight: item[0] === year ? "bold" : "" }}
                  >
                    {toDollar(parseInt(item[1]?.["SARaward"])) || emdash}
                  </td>
                )}
                <td
                  className="compensation d-none d-md-table-cell"
                  style={{ fontWeight: item[0] === year ? "bold" : "" }}
                >
                  {toDollar(parseInt(item[1]?.["nonEquity"])) || emdash}
                </td>
                <td
                  className="compensation d-none d-md-table-cell"
                  style={{ fontWeight: item[0] === year ? "bold" : "" }}
                >
                  {toDollar(parseInt(item[1]?.["pension"])) || emdash}
                </td>
                <td
                  className="compensation d-none d-md-table-cell"
                  style={{ fontWeight: item[0] === year ? "bold" : "" }}
                >
                  {toDollar(parseInt(item[1]?.["other"])) || emdash}
                </td>
                <td
                  className="compensation"
                  style={{ fontWeight: item[0] === year ? "bold" : "" }}
                >
                  {/* {total > 0 ? toDollar(parseInt(total)) : emdash} /Old method adds up numbers*/}
                  {toDollar(parseInt(item[1]?.["total"]))
                    ? toDollar(parseInt(item[1]?.["total"])) +
                      (rowDoesNotEqualTotal ? "**" : "")
                    : emdash}
                </td>
                {tableContainsRowDoesNotEqualTotal && (
                  <td
                    className="compensation"
                    style={{ fontWeight: item[0] === year ? "bold" : "" }}
                  >
                    {/* {total > 0 ? toDollar(parseInt(total)) : emdash} /Old method adds up numbers*/}
                    {rowDoesNotEqualTotal &&
                    toDollar(parseInt(item[1]?.["total"]))
                      ? toDollar(sumTotal)
                      : emdash}
                  </td>
                )}
                {tableContainsRowDoesNotEqualTotal && (
                  <td
                    className="compensation"
                    style={{
                      fontWeight: item[0] === year ? "bold" : "",
                      color:
                        parseInt(item[1]?.["total"]) - parseInt(sumTotal) >= 0
                          ? "green"
                          : "red",
                    }}
                  >
                    {/* {total > 0 ? toDollar(parseInt(total)) : emdash} /Old method adds up numbers*/}
                    {rowDoesNotEqualTotal &&
                    toDollar(parseInt(item[1]?.["total"]))
                      ? parseInt(item[1]?.["total"]) - parseInt(sumTotal) < 0
                        ? `(${toDollar(
                            Math.abs(parseInt(item[1]?.["total"]) - sumTotal),
                          )})`
                        : toDollar(
                            Math.abs(parseInt(item[1]?.["total"]) - sumTotal),
                          )
                      : emdash}
                  </td>
                )}
              </tr>
            );
          });
        // }
      });
  }, [
    executives,
    includesPSUs,
    includesRSUs,
    includesSARaward,
    info,
    positions,
    showRankWithinCompany,
    year,
    tableContainsRowDoesNotEqualTotal,
    PDFprocessing,
  ]);
  // console.log(data);
  //<Bar data={data} options={options} />
  // console.log(info);
  const handleYearChange = (e) => {
    setYear(e.target.value);
  };
  useMemo(() => {
    const newYears = [];
    try {
      for (const data of executives) {
        if (data?.compensation) {
          for (const year of Object.keys(data?.compensation)) {
            if (!newYears.includes(year)) {
              newYears.push(year);
            }
          }
        }
      }
    } catch {
      newYears.push("2021");
    }
    setYearsWithData(newYears.sort((a, b) => parseInt(b) - parseInt(a)));
  }, [executives]);

  useEffect(() => {
    setYear(yearsWithData[0]);
  }, [yearsWithData, executives]);

  const yearsOptions = useMemo(() => {
    return yearsWithData.map((year, index) => (
      <option value={year} key={index}>
        {year}
      </option>
    ));
  }, [yearsWithData]);

  useEffect(() => {
    const dataPeekKeys = info?.DataPeek
      ? Object.keys(info?.DataPeek).reverse()
      : [];

    let reportYearEqualsDataYear = true;
    if (
      dataPeekKeys.length &&
      yearsWithData.length &&
      yearsWithData[0] !== dataPeekKeys[0] &&
      yearsWithData.length > dataPeekKeys.length // means there is missing data, so just display the report years instead of guessing
    ) {
      reportYearEqualsDataYear = false;
    }
    setDataPeekLinks(
      dataPeekKeys.map((year, index) => {
        const content = (
          <div key={index}>
            {info.DataPeek[year]?.url ? (
              <a
                href={info.DataPeek[year].url}
                target="_blank"
                rel="noreferrer"
              >
                <img
                  src={`/data_peek/${info.DataPeek[year].file}?ver=${version}`}
                  style={{ width: "750px" }}
                  alt={`DataPeek data for ${info?.Ticker} ${
                    reportYearEqualsDataYear ? year : year - 1
                  }`}
                />
              </a>
            ) : (
              <img
                src={`/data_peek/${info.DataPeek[year].file}?ver=${version}`}
                style={{ width: "750px" }}
                alt={`DataPeek data for ${info?.Ticker} ${
                  reportYearEqualsDataYear ? year : year - 1
                }`}
              />
            )}
          </div>
        );
        return (
          <div
            style={{
              width: "100%",
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "flex-end",
            }}
            key={year}
          >
            <Popover
              // style={{ width: 100 }}
              content={content}
              placement="bottomRight"
              title={
                <>
                  <p>
                    DataPeek - {info?.Ticker}{" "}
                    {reportYearEqualsDataYear ? year : year - 1}
                  </p>
                  {info.DataPeek[year]?.conversion && (
                    <p>
                      Data in this table was converted at a rate of{" "}
                      {info.DataPeek[year].conversion.amount}{" "}
                      {info.DataPeek[year].conversion.type} based on report date{" "}
                      {new Date(
                        info.DataPeek[year].conversion?.date,
                      ).toLocaleDateString()}
                    </p>
                  )}
                </>
              }
            >
              <div
                style={{
                  width: 200,
                  cursor: "pointer",
                  paddingRight: "2px",
                  paddingTop: "0px",
                  paddingBottom: "4px",
                  marginTop: "-6px",
                }}
              >
                <p
                  style={{
                    textAlign: "right",
                    color: "var(--blue)",
                  }}
                >
                  DataPeek - {info?.Ticker}{" "}
                  {reportYearEqualsDataYear ? year : year - 1}
                </p>
              </div>
            </Popover>
          </div>
        );
      }),
    );
  }, [info, version, yearsWithData]);

  const [pageContainsNewComp, setPageContainsNewComp] = useState(false);
  useEffect(() => {
    setPageContainsNewComp(
      executives.some((key) => isFirstYearOfComp(key, year)),
    );
  }, [executives, year]);

  const sortByDate = (a, b) => {
    const dateA = a.Date ? new Date(a.Date) : null;
    const dateB = b.Date ? new Date(b.Date) : null;
    if (dateA && dateB) {
      return dateB.getTime() - dateA.getTime();
    } else if (dateA) {
      return -1; // a is a valid date, but b is not
    } else if (dateB) {
      return 1; // b is a valid date, but a is not
    } else {
      return 0; // neither a nor b is a valid date
    }
  };

  const ParagraphsFrom8k502 = () => {
    const paragraphsFrom8k502Data =
      paragraphsFrom8k502?.["Ticker"] === info?.Ticker
        ? paragraphsFrom8k502?.["502_Data"]
        : {};
    return (
      <div>
        <h2>Recent Events & Compensation Changes</h2>
        {paragraphsFrom8k502Data?.length ? (
          <div>
            {paragraphsFrom8k502Data
              .sort((a, b) => sortByDate(a, b))
              .map((data, key) => {
                const paragraphsFromReport = data?.ReleventParagraphs?.filter(
                  (paragraph) => paragraph.length > 10,
                );
                return (
                  <div style={{ fontSize: "1.15em" }} key={key}>
                    <b style={{ fontSize: "1.3em" }}>
                      {new Date(data.Date).toLocaleDateString()}:{" "}
                      <a
                        style={{
                          color: "var(--link-blue)",
                          textDecoration: "none",
                          textAlign: "center",
                        }}
                        target="_blank"
                        href={`${data?.URL}`}
                        rel="noreferrer"
                      >
                        [View Source]
                      </a>
                    </b>
                    <ul>
                      {paragraphsFromReport.map((paragraph, key) => (
                        <li key={key} style={{ fontSize: "14px" }}>
                          {paragraph}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
          </div>
        ) : (
          <div>There are no recent compensation changes for this company.</div>
        )}
      </div>
    );
  };

  useEffect(() => {
    setPayRatioIsCalculated(getDatapoint("PayRatio")?.calculated);
  }, [getDatapoint, info, year]);

  const [printPDFasMultiplePages, setPrintPDFasMultiplePages] = useState(false);

  const elementsIDsForPDFExport = useMemo(() => {
    // main component includes the company info and the first component on the page
    // (since they are formatted side-by-side on each page)
    const elementIDs = [["main-component"]];
    switch (pageModeSelected) {
      case "Company Overview":
        elementIDs.push(["overview-graphs"], ["recent-comp-changes"]);
        break;
      case "Company Calculators":
        elementIDs.push(["peer-group-comparison"], ["pvp-section"]);
        break;
      case "Peer Group Analysis":
        if (printPDFasMultiplePages) {
          elementIDs.push(["peer-group-table"]);
        }
        break;
      default:
        break;
    }
    return elementIDs;
  }, [pageModeSelected, printPDFasMultiplePages]);

  /* To change and reset width of component */
  useEffect(() => {
    const sleep = (milliseconds) => {
      return new Promise((resolve) => setTimeout(resolve, milliseconds));
    };
    const handleRerender = async (milliseconds) => await sleep(milliseconds); // Time to re-render page for formatting, may have to adjust)
    if (
      pageModeSelected === "Company Overview" &&
      document.getElementById("barChart") &&
      document.getElementById("lineGraph") &&
      document.getElementById("overview-graphs")
    ) {
      /* To change width of component (if doesn't fit on PDF page) */
      document.body.style.width = PDFprocessing ? "1800px" : "100%";
      document.getElementById("overview-graphs").style.height = PDFprocessing
        ? "1200px"
        : "";
      document.getElementById("barChart").style.width = PDFprocessing
        ? "100%"
        : "50%";

      document.getElementById("lineGraph").style.width = PDFprocessing
        ? "100%"
        : "45%";
    }
    if (PDFprocessing) {
      handleRerender(1500);
    }
  }, [pageModeSelected, PDFprocessing]);

  useEffect(() => {
    if (pageModeSelected !== "Company Overview") {
      setYear(yearsWithData[0]);
    }
  }, [pageModeSelected, yearsWithData]);

  const companyInfoComponent = useMemo(() => {
    return (
      <div className="col-lg-3 mb-4 company-info">
        <h1>{info?.Company}</h1>
        {pageModeSelected === "Company Overview" && (
          <div>
            Year:{" "}
            {!PDFprocessing ? (
              <select
                name="year"
                onChange={handleYearChange}
                value={year}
                style={{ textAlign: "center", height: 30 }}
              >
                {yearsOptions}
              </select>
            ) : (
              <span style={{ fontWeight: "bold" }}>{year}</span>
            )}
          </div>
        )}

        {pageModeSelected === "Company Overview" && !PDFprocessing && (
          <div style={{ paddingTop: 10 }}>
            <Checkbox
              checked={showRankWithinCompany}
              onChange={() => setShowRankWithinCompany(!showRankWithinCompany)}
            >
              Show Executive Rank (within {info?.Ticker})
            </Checkbox>
          </div>
        )}
        <br />
        <div>
          Stock Ticker:{" "}
          <a
            target="_blank"
            href={`${ETRADE_URL}${info?.Ticker}`}
            rel="noreferrer"
          >
            {info?.Ticker}
          </a>
        </div>
        {info?.AlternateTickers?.length ? (
          <div>
            Alternate Ticker
            {info?.AlternateTickers?.length > 2 && "s"}:{" "}
            <span>
              {info?.AlternateTickers.filter(
                (ticker) => ticker !== info?.Ticker,
              ).join(", ")}
            </span>
          </div>
        ) : null}
        <div>
          Sector: <span>{info?.Sector || "Other"}</span>
        </div>
        <div>
          Industry: <span>{info?.Industry || "Other"}</span>
        </div>
        <div>
          SIC: <span>{info?.SIC || "N/A"}</span>
        </div>
        <div>
          <div>
            {year} Assets:{" "}
            <span>
              {getMeasure("Assets")?.value !== "N/A"
                ? "$" + getMeasure("Assets")?.value?.toLocaleString()
                : "N/A"}
            </span>
          </div>
          {year} Revenue:{"  "}
          <span>
            {getDatapoint("Revenue")?.value !== "N/A"
              ? "$" + getDatapoint("Revenue")?.value?.toLocaleString()
              : info?.Measures?.revenue?.[year]
                ? "$" + info?.Measures?.revenue?.[year]?.toLocaleString()
                : "N/A"}
          </span>
        </div>
        <div>
          {year} Profit (Loss):{" "}
          {renderNetIncome(
            info?.Measures?.netIncome?.[year] ||
              getDatapoint("NetIncome")?.value ||
              null,
          )}
        </div>
        {getDatapoint("PayRatio")?.value !== "N/A" && (
          <div>
            {year} CEO Pay Ratio:{" "}
            {getDatapoint("PayRatio")?.value !== "N/A" ? (
              payRatioIsCalculated ? (
                <Tooltip title={payRatioCalculatedMessage(info, year)}>
                  <span style={{ cursor: "help" }}>
                    {formatMeasure(getDatapoint("PayRatio").value, "ratio")} : 1
                    {payRatioIsCalculated && <sup> *</sup>}
                  </span>
                </Tooltip>
              ) : (
                <span>
                  {formatMeasure(getDatapoint("PayRatio").value, "ratio")} : 1
                </span>
              )
            ) : (
              <span>N/A</span>
            )}
          </div>
        )}
        <div>
          Current Market Cap:{" "}
          <span>
            {fiscalMarketCap >= 0
              ? "$" + parseInt(fiscalMarketCap).toLocaleString()
              : marketCap
                ? "$" + parseInt(marketCap).toLocaleString()
                : "N/A"}
          </span>
        </div>
        <div>
          Current Employee Count:{" "}
          <span>
            {info?.Employees &&
            parseInt(info?.Employees.toString().replace(",", "")) >= 0
              ? info?.Employees?.toLocaleString()
              : "N/A"}
          </span>
        </div>
        <div>
          Location: <span>{info?.Address || "N/A"}</span>
        </div>
        {/* <div style={{ marginTop: 6 }}>
          <span>
            {payRatioIsCalculated && payRatioCalculatedMessage(info, year)}
          </span>
        </div> */}
        <div className="exportPDF" style={{ marginTop: 6 }}>
          <ExportPDFButton
            setPDFprocessing={setPDFprocessing}
            elementIDgroups={elementsIDsForPDFExport}
          />
        </div>
        <div style={{ marginTop: 12, fontWeight: "normal" }}>
          {typeof companyProfile?.[info?.Ticker]?.shortDescription ===
            "string" &&
            pageModeSelected === "Company Overview" && (
              <Tooltip
                title={`Click to show ${!showShortDescription ? "short" : "long"} description`}
              >
                <div
                  onClick={() => setShowShortDescription(!showShortDescription)}
                  style={{ cursor: "pointer" }}
                >
                  <p>Company Description:</p>
                  {/* Description:{" "} */}
                  <span>
                    {showShortDescription ? (
                      companyProfile?.[info?.Ticker]?.shortDescription
                        .split(/\. (?=[A-Z])/)
                        .map((item, index) => (
                          <p style={{ marginTop: index === 0 ? 0 : 5 }}>
                            {item +
                              (index !==
                              companyProfile?.[
                                info?.Ticker
                              ]?.shortDescription.split(/\. (?=[A-Z])/).length -
                                1
                                ? ". "
                                : "")}
                          </p>
                        ))
                    ) : typeof companyProfile?.[info?.Ticker]?.description ===
                      "string" ? (
                      companyProfile?.[info?.Ticker]?.description
                        .split(/\. (?=[A-Z])/)
                        .map((item, index) => (
                          <p style={{ marginTop: index === 0 ? 0 : 5 }}>
                            {item +
                              (index !==
                              companyProfile?.[info?.Ticker]?.description.split(
                                /\. (?=[A-Z])/,
                              ).length -
                                1
                                ? ". "
                                : "")}
                          </p>
                        ))
                    ) : (
                      <span>
                        No Long Description Available. Click to view short
                        Description.
                      </span>
                    )}
                  </span>
                </div>
              </Tooltip>
            )}
        </div>
      </div>
    );
  }, [
    PDFprocessing,
    elementsIDsForPDFExport,
    info,
    marketCap,
    pageModeSelected,
    getDatapoint,
    getMeasure,
    payRatioIsCalculated,
    showRankWithinCompany,
    year,
    yearsOptions,
    companyProfile,
    showShortDescription,
    fiscalMarketCap,
  ]);

  const [reportFiscalYear, setReportFiscalYear] = useState("FY 2024");

  useEffect(() => {
    if (pageModeSelected) {
      setReportFiscalYear("FY 2025");
    }
  }, [pageModeSelected]);

  const [generalInfoParams, setGeneralInfoParams] = useState([]);
  const [taxTableParams, setTaxTableParams] = useState([]);
  const [optionsCalculatorValues, setOptionsCalculatorValues] = useState([]);
  const [RSUCalculatorValues, setRSUCalculatorValues] = useState([]);
  const [trancheTableOptionValues, setTrancheTableOptionValues] = useState([]);
  const [trancheTableRSUValues, setTrancheTableRSUValues] = useState([]);
  const [initialized, setInitalized] = useState(false);
  const [firstLoad, setFirstLoad] = useState(true);

  const calculatorPages = useMemo(
    () => [
      "Initial Parameters",
      "Options Schedule",
      "RSU Schedule",
      "Combined Schedule",
      "Simulation & Strategies",
      "Retirement Date (Coming Soon)",
    ],
    [],
  );
  const [allTrancheTableValues, setAllTrancheTableValues] = useState(
    Object.fromEntries(calculatorPages.map((pageName) => [pageName, {}])),
  );

  const FY_Options = useMemo(() => {
    const defaultOptions = ["FY 2024", "FY 2025"];
    if (
      compAIaccess &&
      user?.enableAImodelOptions &&
      ["AXP", "META", "MMM", "TSLA", "TXG"].includes(info?.Ticker)
    ) {
      defaultOptions.push("FY 2025-large");
      defaultOptions.push("FY 2025-Model-O");
      defaultOptions.push("FY 2025-Model-T-3-latest");
      defaultOptions.push("FY 2025-Model-G-flash");
    }
    return defaultOptions;
  }, [compAIaccess, user, info]);

  return (
    <div className="overview container mb-3" style={{ minHeight: "90vh" }}>
      <div
        className="container"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: "20px",
          marginTop: "100px",
        }}
      >
        <Radio.Group
          // defaultValue={initialSelectorOptions[0]}
          size="medium"
          onChange={(e) => {
            setPageModeSelected(e.target.value);
          }}
          value={pageModeSelected}
          style={{ justifyContent: "center", textAlign: "center" }}
        >
          {pageModeOptions.map((option, index) => (
            <Tooltip
              title={hoverTextMap[option]}
              overlayClassName="tooltip-pass-through"
              key={index}
            >
              <div style={{ display: "inline-block", margin: 0 }}>
                <Link
                  to={
                    reverseHashMap?.[option]
                      ? `${location.pathname}#${reverseHashMap[option]}`
                      : `${location.pathname}`
                  }
                  onClick={() => {
                    setPageModeSelected(option);
                    setReportFiscalYear("FY 2025");
                  }}
                  style={{ color: "inherit" }}
                  key={index}
                >
                  <Radio.Button
                    value={option}
                    key={option}
                    className="custom-radio-button"
                    style={{ margin: 5 }}
                  >
                    {option}
                  </Radio.Button>
                </Link>
              </div>
            </Tooltip>
          ))}
        </Radio.Group>
      </div>

      <div>
        {!loading && companyNotFound ? (
          <div>
            <NotFound />
          </div>
        ) : loading ? (
          <center
            style={{
              minHeight: "500px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <PuffLoader color={"var(--loader)"} loading={loading} size={100} />
          </center>
        ) : (
          <div>
            <div className="container">
              <div className="row">
                {needsReportFiscalYearSelector.includes(pageModeSelected) && (
                  <RadioSelector
                    options={FY_Options}
                    state={reportFiscalYear}
                    setState={setReportFiscalYear}
                    heading="Report Fiscal Year:"
                    style={{ justifyContent: "center", marginBottom: 20 }}
                  />
                )}
                <div
                  className="row"
                  id={
                    !["Company Calculators", "Peer Group Analysis"].includes(
                      pageModeSelected,
                    )
                      ? "main-component"
                      : ""
                  }
                >
                  {
                    // All pages besides these 2 have the company overview imported for formatting, still show here to display upsell blurbs properly
                    ((!isTier2 &&
                      !peerGroupAccess &&
                      !companyCalculatorAccess) ||
                      ![
                        "Company Calculators",
                        "Peer Group Analysis",
                        "Voting Outcomes",
                        "Equity Compensation Modeling (ECM)",
                        peerGroupAccess || companyCalculatorAccess
                          ? ""
                          : "Board Pay",
                      ].includes(pageModeSelected)) &&
                      companyInfoComponent
                  }
                  {
                    //Special case for customers who can access peer group table, still show the company info while showing upsell blurbs
                    !isTier2 &&
                      !companyCalculatorAccess &&
                      peerGroupAccess &&
                      ["Company Calculators"].includes(pageModeSelected) &&
                      companyInfoComponent
                  }
                  {pageModeSelected === "Company Calculators" && (
                    <CompanyCalculator
                      companyInfo={info}
                      companyInfoComponent={companyInfoComponent}
                      companyExecutives={executives}
                      PDFprocessing={PDFprocessing}
                    />
                  )}
                  {pageModeSelected === "Company Financials" && (
                    <CompanyFinancials
                      companyInfo={info}
                      PDFprocessing={PDFprocessing}
                    />
                  )}
                  {pageModeSelected === "Voting Outcomes" && (
                    <VotingOutcomes
                      companyInfo={info}
                      PDFprocessing={PDFprocessing}
                    />
                  )}
                  {pageModeSelected === "Executive Perks" && (
                    <ExecutivePerks
                      companyInfo={info}
                      reportYear={reportFiscalYear}
                      PDFprocessing={PDFprocessing}
                      setReportFiscalYear={setReportFiscalYear}
                    />
                  )}
                  {pageModeSelected === "Board Pay" && (
                    <BoardPay
                      companyInfo={info}
                      PDFprocessing={PDFprocessing}
                    />
                  )}
                  {pageModeSelected === "Board Pay Structure" && (
                    <BoardPayStructure
                      companyInfo={info}
                      reportYear={reportFiscalYear}
                      PDFprocessing={PDFprocessing}
                      setReportFiscalYear={setReportFiscalYear}
                    />
                  )}
                  {pageModeSelected === "Peer Group Analysis" && (
                    <PeerGroupAnalysis
                      companyInfo={info}
                      companyInfoComponent={companyInfoComponent}
                      PDFprocessing={PDFprocessing}
                      printPDFasMultiplePages={printPDFasMultiplePages}
                      setPrintPDFasMultiplePages={setPrintPDFasMultiplePages}
                    />
                  )}
                  {pageModeSelected === "Options Calculator" && (
                    <OptionsCalculators companyInfo={info} />
                  )}
                  {pageModeSelected === "rTSR Calculators" && (
                    <RTSRCalculators companyInfo={info} />
                  )}
                  {pageModeSelected ===
                    "Equity Compensation Modeling (ECM)" && (
                    <Calculator10b51
                      companyInfo={info}
                      PDFprocessing={PDFprocessing}
                      calculatorPages={calculatorPages}
                      generalInfoParams={generalInfoParams}
                      setGeneralInfoParams={setGeneralInfoParams}
                      taxTableParams={taxTableParams}
                      setTaxTableParams={setTaxTableParams}
                      optionsCalculatorValues={optionsCalculatorValues}
                      setOptionsCalculatorValues={setOptionsCalculatorValues}
                      RSUCalculatorValues={RSUCalculatorValues}
                      setRSUCalculatorValues={setRSUCalculatorValues}
                      trancheTableOptionValues={trancheTableOptionValues}
                      setTrancheTableOptionValues={setTrancheTableOptionValues}
                      trancheTableRSUValues={trancheTableRSUValues}
                      setTrancheTableRSUValues={setTrancheTableRSUValues}
                      allTrancheTableValues={allTrancheTableValues}
                      setAllTrancheTableValues={setAllTrancheTableValues}
                      initialized={initialized}
                      setInitalized={setInitalized}
                      firstLoad={firstLoad}
                      setFirstLoad={setFirstLoad}
                    />
                  )}
                  {pageModeSelected === "Incentive Structure" && (
                    <IncentiveStructure
                      companyInfo={info}
                      reportYear={reportFiscalYear}
                      PDFprocessing={PDFprocessing}
                      setReportFiscalYear={setReportFiscalYear}
                    />
                  )}
                  {pageModeSelected === "Risk" && (
                    <RiskModule
                      companyInfo={info}
                      reportYear={reportFiscalYear}
                      PDFprocessing={PDFprocessing}
                      setReportFiscalYear={setReportFiscalYear}
                    />
                  )}
                  {pageModeSelected === "Compensation Decisions" && (
                    <CompensationDecisions
                      companyInfo={info}
                      reportYear={reportFiscalYear}
                      setReportFiscalYear={setReportFiscalYear}
                    />
                  )}
                  {pageModeSelected === "Grants of Plan-Based Awards" && (
                    <PlanBasedAwards
                      companyInfo={info}
                      reportYear={reportFiscalYear}
                      setReportFiscalYear={setReportFiscalYear}
                    />
                  )}
                  {pageModeSelected === "Severance/Change in Control" && (
                    <ChangeInControls
                      companyInfo={info}
                      reportYear={reportFiscalYear}
                      PDFprocessing={PDFprocessing}
                      setReportFiscalYear={setReportFiscalYear}
                    />
                  )}
                  {pageModeSelected === "Equity Pool Usage" && (
                    <EquityPoolUsage
                      companyInfo={info}
                      FY_Options={FY_Options}
                      PDFprocessing={PDFprocessing}
                    />
                  )}
                  {pageModeSelected === "Target Total Direct Compensation" && (
                    <TargetTotalDirectCompensation
                      companyInfo={info}
                      reportYear={reportFiscalYear}
                      PDFprocessing={PDFprocessing}
                      setReportFiscalYear={setReportFiscalYear}
                    />
                  )}
                  {pageModeSelected === "Comp AI®" && (
                    <CompAI companyInfo={info} />
                  )}
                  {pageModeSelected === "Company Overview" && (
                    <div className="col-lg-9 executives p-0">
                      {PDFprocessing ? (
                        <h3>Executive Compensation Overview</h3>
                      ) : (
                        <div className="row">
                          <div className="col-md d-flex justify-content-start align-items-end pb-3">
                            <p
                              style={{
                                fontSize: 18,
                                lineHeight: 1.4,
                              }}
                            >
                              CSV&nbsp;Export:&nbsp;
                            </p>
                            <Select
                              options={CSVExportOptions}
                              value={CSVExportOptionSelected}
                              onChange={(value) =>
                                setCSVExportOptionSelected(value)
                              }
                              style={{
                                width:
                                  (customUserPeerGroups &&
                                    Object.keys(customUserPeerGroups).length) ||
                                  CSVExportOptions.some(
                                    (item) =>
                                      item.label.includes("Performance") ||
                                      item.label.includes("Compensation"),
                                  )
                                    ? 230
                                    : 150,
                                textAlign: "center",
                                paddingRight: 5,
                              }}
                              dropdownStyle={{ textAlign: "center" }}
                              defaultValue={"company"}
                            ></Select>
                            {CSVExportOptionSelected === "company" && (
                              <Button
                                onClick={() =>
                                  exportToCSV(
                                    formatExecutiveDataForExport(executives),
                                    `${info?.Ticker} ${
                                      CSVExportOptions.filter(
                                        (opt) =>
                                          opt.value === CSVExportOptionSelected,
                                      )[0].label
                                    } Executive Data ${
                                      new Date().toString().split("GMT-")[0]
                                    }.csv`,
                                  )
                                }
                              >
                                Export Company Data to CSV
                              </Button>
                            )}
                            {CSVExportOptionSelected === "analyst" && (
                              <Button
                                onClick={() =>
                                  exportToCSV(
                                    formatExecutiveDataForExport(
                                      executives.concat(
                                        analystCohortExecutives,
                                      ),
                                      true,
                                    ),
                                    `${info?.Ticker} ${
                                      CSVExportOptions.filter(
                                        (opt) =>
                                          opt.value === CSVExportOptionSelected,
                                      )[0].label
                                    } Executive Data ${
                                      new Date().toString().split("GMT-")[0]
                                    }.csv`,
                                  )
                                }
                              >
                                Export Analyst Cohort Data to CSV
                              </Button>
                            )}
                            {CSVExportOptionSelected === "proxy" && (
                              <Button
                                onClick={() =>
                                  exportToCSV(
                                    formatExecutiveDataForExport(
                                      executives.concat(proxyCohortExecutives),
                                      true,
                                    ),
                                    `${info?.Ticker} ${
                                      CSVExportOptions.filter(
                                        (opt) =>
                                          opt.value === CSVExportOptionSelected,
                                      )[0].label
                                    } Executive Data ${
                                      new Date().toString().split("GMT-")[0]
                                    }.csv`,
                                  )
                                }
                              >
                                Export Proxy Cohort Data to CSV
                              </Button>
                            )}
                            {CSVExportOptionSelected ===
                              "proxyCompensation" && (
                              <Button
                                onClick={() =>
                                  exportToCSV(
                                    formatExecutiveDataForExport(
                                      executives.concat(
                                        compensationCohortExecutives,
                                      ),
                                      true,
                                    ),
                                    `${info?.Ticker} ${
                                      CSVExportOptions.filter(
                                        (opt) =>
                                          opt.value === CSVExportOptionSelected,
                                      )[0].label
                                    } Executive Data ${
                                      new Date().toString().split("GMT-")[0]
                                    }.csv`,
                                  )
                                }
                              >
                                Export Proxy Cohort Data to CSV
                              </Button>
                            )}
                            {CSVExportOptionSelected === "compAIproxy" && (
                              <Button
                                onClick={() =>
                                  exportToCSV(
                                    formatExecutiveDataForExport(
                                      executives.concat(
                                        compAIProxyCohortExecutives,
                                      ),
                                      true,
                                    ),
                                    `${info?.Ticker} ${
                                      CSVExportOptions.filter(
                                        (opt) =>
                                          opt.value === CSVExportOptionSelected,
                                      )[0].label
                                    } Executive Data ${
                                      new Date().toString().split("GMT-")[0]
                                    }.csv`,
                                  )
                                }
                              >
                                Export Proxy Cohort Data to CSV
                              </Button>
                            )}
                            {CSVExportOptionSelected ===
                              "compAIproxyCompensation" && (
                              <Button
                                onClick={() =>
                                  exportToCSV(
                                    formatExecutiveDataForExport(
                                      executives.concat(
                                        compAICompensationCohortExecutives,
                                      ),
                                      true,
                                    ),
                                    `${info?.Ticker} ${
                                      CSVExportOptions.filter(
                                        (opt) =>
                                          opt.value === CSVExportOptionSelected,
                                      )[0].label
                                    } Executive Data ${
                                      new Date().toString().split("GMT-")[0]
                                    }.csv`,
                                  )
                                }
                              >
                                Export Proxy Cohort Data to CSV
                              </Button>
                            )}
                            {CSVExportOptionSelected ===
                              "compAIproxyPerformance" && (
                              <Button
                                onClick={() =>
                                  exportToCSV(
                                    formatExecutiveDataForExport(
                                      executives.concat(
                                        compAIPerformanceCohortExecutives,
                                      ),
                                      true,
                                    ),
                                    `${info?.Ticker} ${
                                      CSVExportOptions.filter(
                                        (opt) =>
                                          opt.value === CSVExportOptionSelected,
                                      )[0].label
                                    } Executive Data ${
                                      new Date().toString().split("GMT-")[0]
                                    }.csv`,
                                  )
                                }
                              >
                                Export Proxy Cohort Data to CSV
                              </Button>
                            )}
                            {CSVExportOptionSelected === "proxyPerformance" && (
                              <Button
                                onClick={() =>
                                  exportToCSV(
                                    formatExecutiveDataForExport(
                                      executives.concat(
                                        performanceCohortExecutives,
                                      ),
                                      true,
                                    ),
                                    `${info?.Ticker} ${
                                      CSVExportOptions.filter(
                                        (opt) =>
                                          opt.value === CSVExportOptionSelected,
                                      )[0].label
                                    } Executive Data ${
                                      new Date().toString().split("GMT-")[0]
                                    }.csv`,
                                  )
                                }
                              >
                                Export Proxy Cohort Data to CSV
                              </Button>
                            )}
                            {isCustomPeerGroup(CSVExportOptionSelected) && (
                              <Button
                                onClick={() =>
                                  exportToCSV(
                                    formatExecutiveDataForExport(
                                      executives.concat(
                                        allExecutives.filter((exec) =>
                                          CSVExportOptions.find(
                                            (item) =>
                                              item.label ===
                                              CSVExportOptionSelected,
                                          ).tickers.includes(exec.Ticker),
                                        ),
                                      ),
                                      true,
                                    ),
                                    `${info?.Ticker} ${
                                      CSVExportOptions.filter(
                                        (opt) =>
                                          opt.value === CSVExportOptionSelected,
                                      )[0].label
                                    } Executive Data ${
                                      new Date().toString().split("GMT-")[0]
                                    }.csv`,
                                  )
                                }
                              >
                                Export Custom Cohort Data to CSV
                              </Button>
                            )}
                          </div>
                          <div className="col-md">{dataPeekLinks}</div>
                        </div>
                      )}
                      <table className="table table-striped table-bordered">
                        <thead>
                          <tr>
                            <th>
                              {showRankWithinCompany
                                ? `Name, Principal Positions, and Rank (within ${info.Ticker}) in ${year}`
                                : `Name and Principal Positions in ${year}`}
                            </th>
                            <th>Year</th>
                            <th className="d-none d-md-table-cell">
                              Base Salary
                            </th>
                            <th className="d-none d-md-table-cell">
                              Bonus Payout
                            </th>
                            <th className="d-none d-md-table-cell">
                              Stock Awards
                            </th>
                            {includesRSUs && (
                              <th className="d-none d-md-table-cell">
                                Restricted Stock Unit Awards
                              </th>
                            )}
                            {includesPSUs && (
                              <th className="d-none d-md-table-cell">
                                Performance Stock Unit Awards
                              </th>
                            )}
                            <th className="d-none d-md-table-cell">
                              Option Awards
                            </th>
                            {includesSARaward && (
                              <th className="d-none d-md-table-cell">
                                SAR Awards
                              </th>
                            )}
                            <th className="d-none d-md-table-cell">
                              Non-Equity Incentive Plan Compensation
                            </th>
                            <th className="d-none d-md-table-cell">
                              Change in Pension Value & Nonqualified Deferred
                              Compensation Earnings
                            </th>
                            <th className="d-none d-md-table-cell">
                              All Other Compensation
                            </th>
                            <th>
                              <b>Total Compensation</b>
                            </th>
                            {tableContainsRowDoesNotEqualTotal && (
                              <th>
                                <b>
                                  Sum of Compensation Components (if
                                  discrepancy)
                                </b>
                              </th>
                            )}
                            {tableContainsRowDoesNotEqualTotal && (
                              <th>
                                <b>
                                  Discrepancy (Reported Total - Sum of
                                  Components)
                                </b>
                              </th>
                            )}
                          </tr>
                        </thead>
                        <tbody>{renderCompensation}</tbody>
                      </table>
                      <div>
                        {pageContainsNewComp
                          ? `* ${year} was this executive's first year of compensation.`
                          : ""}
                      </div>
                      <div>
                        {tableContainsRowDoesNotEqualTotal
                          ? `** C-Suite Comp detects a potential data discrepancy for this officer as the reported sub-components of their pay do not sum to the reported total.`
                          : ""}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {pageModeSelected === "Company Overview" && (
              <div>
                <div className="c-table">
                  <div className="graphs" id="overview-graphs">
                    {PDFprocessing && (
                      <h3 style={{ width: "100%" }}>
                        Executive Compensation Graphs
                      </h3>
                    )}
                    <BarGraphOverview
                      data={executives}
                      year={year}
                      setYear={setYear}
                      yearsOptions={yearsOptions}
                      company={info?.Company}
                      id="barChart"
                      PDFprocessing={PDFprocessing}
                    />
                    <LineGraph
                      data={lineGroup}
                      currentYear={year}
                      company={info?.Company}
                      id="lineGraph"
                      PDFprocessing={PDFprocessing}
                    />
                  </div>
                </div>
                <div className="pt-0">
                  <DisclosureSearchBar companyInfo={info} />
                  <hr className="mt-4" />
                </div>
                <div id="recent-comp-changes">
                  <ParagraphsFrom8k502 />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
